import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { getRankIcon } from "@/lib/streakRanks";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useIsMobile } from "@/hooks/use-mobile";
import GoldSparkleCanvas from "@/lib/GoldSparkleCanvas";

interface RankCardProps {
  index: number;
  title: string;
  description: string;
  isCurrentRank?: boolean;
  className?: string;
  showSparkles?: boolean;
}

export const RankCard = ({
  index,
  title,
  description,
  isCurrentRank = false,
  className = "",
  showSparkles = false
}: RankCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const safeIndex = Math.max(1, Math.min(index || 1, 30));
  const safeTitle = title || `Rank ${safeIndex}`;
  const safeDescription = description || `This is rank ${safeIndex}`;

  const getColorScheme = (idx: number) => {
    const colorSchemes = [{
      primary: "#de0046",
      secondary: "#de0046",
      accent: "#de0046",
      gradient: "from-rose-500 via-rose-400 to-transparent"
    }, {
      primary: "#4361EE",
      secondary: "#3A0CA3",
      accent: "#4CC9F0",
      gradient: "from-blue-500 via-blue-400 to-transparent"
    }, {
      primary: "#FB8500",
      secondary: "#FFB703",
      accent: "#FD9E02",
      gradient: "from-amber-400 via-orange-400 to-transparent"
    }, {
      primary: "#8B5CF6",
      secondary: "#7209B7",
      accent: "#B5179E",
      gradient: "from-purple-500 via-violet-400 to-transparent"
    }, {
      primary: "#38B000",
      secondary: "#70E000",
      accent: "#4CBB17",
      gradient: "from-green-400 via-emerald-300 to-transparent"
    }];
    return colorSchemes[(idx - 1) % colorSchemes.length];
  };

  const splitTitle = () => {
    const words = safeTitle.split(' ');
    if (words.length === 1) return {
      firstLine: words[0],
      secondLine: ''
    };
    const firstLine = words[0];
    const secondLine = words.slice(1).join(' ');
    return {
      firstLine,
      secondLine
    };
  };

  const {
    firstLine,
    secondLine
  } = splitTitle();
  const IconComponent = getRankIcon(safeIndex);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => {
      console.error(`Failed to load image for rank ${safeIndex}`);
      setImageError(true);
      setImageLoaded(true);
    };
    
    img.src = `/streak/${safeIndex}.PNG`;
    
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [safeIndex]);

  const colorScheme = getColorScheme(safeIndex);
  
  const keyframes = {
    subtlePulse: `
      @keyframes subtle-pulse {
        0% { box-shadow: 0 0 5px rgba(255, 215, 0, 0.4); }
        50% { box-shadow: 0 0 15px rgba(255, 215, 0, 0.6); }
        100% { box-shadow: 0 0 5px rgba(255, 215, 0, 0.4); }
      }
    `,
    sparkle: `
      @keyframes sparkle-twinkle {
        0% {
          opacity: 0.3;
          transform: scale(0.8) rotate(0deg);
        }
        50% {
          opacity: 0.9;
          transform: scale(1.2) rotate(180deg);
        }
        100% {
          opacity: 0.3;
          transform: scale(0.8) rotate(360deg);
        }
      }
    `
  };

  return (
    <div 
      className={`rank-card-container relative ${className}`}
      style={{
        zIndex: 0,
        overflow: 'visible',
        margin: isCurrentRank ? '1rem 0.5rem' : '0.5rem 0.25rem',
        minHeight: '342px',
        maxWidth: '95%',
        display: 'block',
        boxSizing: 'border-box',
        fontFamily: "'Inter', system-ui, sans-serif"
      }}
    >
      <style>
        {keyframes.subtlePulse}
        {keyframes.sparkle}
      </style>
      
      {isCurrentRank && (
        <div 
          className="absolute left-1/2 -top-7 z-40 overflow-visible"
          style={{
            transform: 'translateX(-50%)',
            marginTop: '-1px'
          }}
        >
          <div 
            className="bg-gradient-to-r from-amber-500 via-amber-300 to-amber-500 text-black font-bold py-1.5 px-12 rounded-t-md shadow-md border-t border-l border-r border-amber-300 min-w-40"
          >
            <div className="flex items-center justify-center gap-2 whitespace-nowrap">
              <Star className="h-4 w-4 flex-shrink-0 fill-black" />
              <span className="text-xs uppercase tracking-wider">CURRENT RANK</span>
              <Star className="h-4 w-4 flex-shrink-0 fill-black" />
            </div>
          </div>
        </div>
      )}
      
      <div className="relative overflow-visible block w-full">
        {showSparkles && imageLoaded && (
          <div className="absolute overflow-visible z-30 inset-0">
            <GoldSparkleCanvas 
              targetRef={cardRef} 
              particleCount={200} 
              padding={12} 
              extraWidth={15} 
              extraHeight={15} 
              borderWidth={5} 
              minBorderWidth={3} 
              maxBorderWidth={6} 
              showDebugBorder={false} 
              starProbability={0.9} 
              outerMargin={5} 
              innerPadding={2} 
            />
          </div>
        )}
        
        <Card 
          ref={cardRef} 
          className="relative overflow-hidden transition-all duration-200 ease-in-out rounded-xl shadow-2xl border-0 block min-h-[304px] w-full bg-gray-900"
          style={{
            marginTop: isCurrentRank ? '1.5rem' : '0',
            outline: `8px solid ${isCurrentRank ? '#F59E0B' : colorScheme.primary}`,
            animation: showSparkles && imageLoaded ? 'subtle-pulse 3s ease-in-out infinite' : 'none'
          }}
        >
          <div 
            ref={containerRef} 
            className="overflow-visible min-h-[304px]"
          >
            <AspectRatio 
              ratio={2 / 3}
              className="w-full overflow-visible"
            >
              <div className="absolute inset-0 min-h-[304px]">
                <div className="absolute inset-0 opacity-70 z-0">
                  <div 
                    className="absolute inset-0 z-0"
                    style={{
                      backgroundImage: `linear-gradient(to top, ${colorScheme.primary}, transparent)`
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-gray-900/40 z-0" />
                
                <div className="absolute inset-0 z-1 overflow-hidden">
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), 
                                       linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px)`,
                      backgroundSize: '10px 10px',
                      opacity: 0.15
                    }}
                  />
                  
                  <div 
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
                      backgroundSize: '12px 12px',
                      backgroundPosition: '0 0, 6px 6px',
                      opacity: 0.2
                    }}
                  />
                  
                  {IconComponent && (
                    <div 
                      className="absolute inset-0 opacity-25"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(9, 1fr)',
                        gridTemplateRows: 'repeat(9, 1fr)',
                        gap: '0',
                        padding: '0'
                      }}
                    >
                      {Array.from({ length: 81 }).map((_, i) => {
                        const row = Math.floor(i / 9);
                        const col = i % 9;
                        const shouldShowIcon = (row % 2 === 0 && col % 2 === 0) || (row % 2 === 1 && col % 2 === 1);
                        
                        return (
                          <div 
                            key={i} 
                            className="flex items-center justify-center"
                            style={{
                              width: '100%',
                              height: '100%',
                              position: 'relative'
                            }}
                          >
                            {shouldShowIcon && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)'
                                }}
                              >
                                <IconComponent size={36} className="text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <div 
                  className="absolute top-0 left-0 right-0 h-[30%] z-10"
                  style={{
                    backgroundColor: colorScheme.primary,
                    clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0% 100%)'
                  }}
                >
                  <div className="absolute top-0 left-[5%]" style={{ marginLeft: "-6px", marginTop:"5px"}}>
                    <div className="flex flex-col pt-px text-left">
                      <h3 
                        className="text-white leading-tight tracking-tight"
                        style={{
                          fontSize: isMobile ? '2.8rem' : '2.8rem',
                          lineHeight: isMobile ? 1.2 : 1.0,
                          fontWeight: 800,
                          letterSpacing: '-0.025em',
                          textAlign: 'left'
                        }}
                      >
                        {firstLine}
                      </h3>
                      {secondLine && (
                        <h3 
                          className="text-white tracking-tight"
                          style={{
                            fontSize: isMobile ? '2.8rem' : '2.8rem',
                            fontWeight: 800,
                            lineHeight: isMobile ? 1.1 : 1.2,
                            letterSpacing: '-0.025em',
                            marginTop: isMobile ? '-0.25rem' : 0,
                            textAlign: 'left'
                          }}
                        >
                          {secondLine}
                        </h3>
                      )}
                    </div>
                  </div>
                  
                  <div className="absolute top-0 right-0 flex flex-col items-center">
                    <div 
                      className="text-white font-bold py-2 rounded-bl-lg flex items-center justify-center"
                      style={{
                        backgroundColor: colorScheme.secondary,
                        fontSize: isMobile ? '1.5rem' : '1.75rem',
                        paddingLeft: '3px',
                        paddingRight: '3px',
                        minWidth: '40px'
                      }}
                    >
                      {safeIndex}
                    </div>
                    <div className="text-white text-[10px] font-medium mt-0.5 px-2 w-full text-center tracking-wider uppercase">
                      RANK
                    </div>
                  </div>
                </div>
                
                <CardContent className="relative z-10 flex flex-col items-center h-full pt-0 px-2">
                  <div className="flex-shrink-0 w-full flex justify-center absolute top-[15%]">
                    <div className="relative w-full px-0 my-[15px]">
                      {imageLoaded && (
                        <img 
                          src={imageError ? "/streak/1.PNG" : `/streak/${safeIndex}.PNG`}
                          alt={`${safeTitle} rank icon`} 
                          onError={(e) => {
                            console.error(`Image load error for rank ${safeIndex}`);
                            const target = e.target as HTMLImageElement;
                            target.src = '/streak/1.PNG';
                            setImageError(true);
                          }} 
                          className="w-[95%] h-auto object-contain max-h-[313px] mx-auto"
                          loading="eager"
                        />
                      )}
                      {!imageLoaded && (
                        <div className="w-[95%] h-[313px] mx-auto bg-gray-800/50 animate-pulse rounded-lg"></div>
                      )}
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[2.5%] left-[5%] right-[5%] bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-lg backdrop-blur-sm border border-gray-700/50 shadow-lg p-2 max-h-[22%] min-h-[18%] flex items-center justify-center my-1">
                    <p 
                      className="text-gray-100 text-center overflow-y-auto max-h-full leading-tight text-shadow"
                      style={{
                        fontSize: isMobile ? '0.95rem' : '0.925rem',
                        fontWeight: isMobile ? 600 : 500,
                        textShadow: '0px 1px 2px rgba(0,0,0,0.2)',
                        fontVariationSettings: "'wght' 500, 'slnt' 0"
                      }}
                    >
                      {safeDescription}
                    </p>
                  </div>
                </CardContent>
              </div>
            </AspectRatio>
          </div>
        </Card>
      </div>
    </div>
  );
};
